/**
 * Chime is not supported, and how to tell whether an account is one.
 *
 * Chime is a neobank, not a bank: it holds deposits at partner banks and issues
 * its members those partners' routing numbers. Our payroll ACH credits do not
 * land in a Chime account, so a nurse who onboards with Chime completes her
 * Direct Deposit section, works visits, and is not paid. That is the worst
 * possible place to discover it, so she has to be told where she types it.
 *
 * Pure, no imports: the API, the mobile app and the admin console all apply the
 * same rule, and each must state it in the same words.
 *
 * ── Why this is two rules and not one ─────────────────────────────────────
 * A routing number identifies the PARTNER BANK, never Chime. The Bancorp Bank
 * and Stride Bank are banking-as-a-service providers behind dozens of app-based
 * accounts and prepaid card programs; Chime is one of them. So a Bancorp
 * routing number means "this might be Chime", never "this is Chime".
 *
 * Refusing on the routing number alone would turn away a nurse with a perfectly
 * payable non-Chime account, telling her it is a Chime account, on a form that
 * gives her no way to say otherwise. That is worse than the problem: she cannot
 * finish onboarding at all.
 *
 * So the two signals are graded by how much they actually prove:
 *
 *   "block"   — she named Chime. Unambiguous, and refused outright.
 *   "confirm" — a partner-bank routing number or name. Suspicious only. She is
 *               shown what it means and has to confirm it is not Chime.
 *
 * The confirmation is the honest version of the ban: it reaches every nurse who
 * would otherwise have gone unpaid (including one who names an unrelated bank
 * and types a Chime routing number, which production has an example of) without
 * stranding anyone we merely suspect.
 */
/**
 * The partner banks behind Chime, by routing number. Chime prints these in its
 * app as the member's own routing number, so this is what a Chime nurse copies
 * into the form — but so does every other program these two banks back.
 */
export const PARTNER_BANK_ROUTING_NUMBERS = {
    "031101279": "The Bancorp Bank",
    "103100195": "Stride Bank"
};
/** Names Chime outright. The only thing here that proves it is Chime. */
const CHIME_NAME_RE = /\bchime\b/i;
/**
 * The partner banks by name. A nurse reading her bank out of the Chime app sees
 * "The Bancorp Bank, N.A." and types that — but a nurse who genuinely banks
 * with Stride or Bancorp types it too, so this only earns a confirmation.
 */
const PARTNER_BANK_NAME_RE = /\bstride\b|\bbancorp\b/i;
/** Strip everything but digits — nurses paste routing numbers with spaces and dashes. */
function digitsOnly(value) {
    return value.replace(/\D/g, "");
}
/** True when she named Chime itself. */
export function isChimeBankName(value) {
    return !!value && CHIME_NAME_RE.test(value);
}
/**
 * True when the routing number belongs to a bank that backs Chime.
 *
 * NOT the same as "this is a Chime account" — these banks back many other
 * programs. Callers must treat it as a prompt to ask, never as proof.
 */
export function isPartnerBankRouting(value) {
    return !!value && digitsOnly(value) in PARTNER_BANK_ROUTING_NUMBERS;
}
/** The partner bank a routing number belongs to, or null. */
export function partnerBankFor(value) {
    if (!value) {
        return null;
    }
    return PARTNER_BANK_ROUTING_NUMBERS[digitsOnly(value)] ?? null;
}
/** Shown when she named Chime: a refusal, in the words she sees everywhere. */
export const UNSUPPORTED_BANK_MESSAGE = "We do not support Chime. Chime accounts do not accept our payroll deposits, " +
    "so entering a Chime account here means you will NOT receive your pay. " +
    "Please enter an account at a traditional bank or credit union. " +
    "If you only have Chime, contact us and we will help you sort it out.";
/** What she has to confirm before a partner-bank account is accepted. */
export const CHIME_CONFIRMATION_LABEL = "I confirm this is not a Chime account";
/**
 * Shown when the account MIGHT be Chime. Says why we are asking rather than
 * implying we know, because most of the time we are wrong about the program —
 * we only know the bank.
 */
export function partnerBankWarning(bankLabel) {
    return (`This routing number belongs to ${bankLabel}, which is the bank behind Chime ` +
        "as well as a number of other app-based accounts. We cannot pay into Chime: " +
        "if this is a Chime account, you will NOT receive your pay. " +
        `If it is not Chime, tick "${CHIME_CONFIRMATION_LABEL}" below and carry on.`);
}
/**
 * What to do about a Direct Deposit answer, or `null` if there is nothing to say.
 *
 * `itemName` is the onboarding item's name ("Bank name", "Bank name (if Other)",
 * "Routing number"), matched loosely so a renamed or re-worded item keeps the
 * rule. Anything else — account number, account type, the signature — is not
 * this function's business and returns `null`.
 */
export function checkBankAnswer(itemName, answerValue) {
    if (!answerValue || answerValue.trim() === "") {
        return null;
    }
    const name = itemName.toLowerCase();
    const isBankNameField = /bank\s*name/.test(name);
    const isRoutingField = /routing/.test(name);
    if (isBankNameField) {
        if (isChimeBankName(answerValue)) {
            return { level: "block", message: UNSUPPORTED_BANK_MESSAGE };
        }
        if (PARTNER_BANK_NAME_RE.test(answerValue)) {
            return { level: "confirm", message: partnerBankWarning(answerValue.trim()) };
        }
    }
    if (isRoutingField) {
        const bank = partnerBankFor(answerValue);
        if (bank) {
            return { level: "confirm", message: partnerBankWarning(bank) };
        }
    }
    return null;
}
//# sourceMappingURL=unsupported-bank.js.map